import { styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { hasCriterionGradingSettings } from 'utils/gradingInstructions';
import { stripHTML } from 'utils/helper';
import SharedCriterionAccordion from './CriterionAccordion';

const getInstructionCriteria = (source) => {
    const criteria = Array.isArray(source?.rubric?.criteria) ? source.rubric.criteria : [];
    const instructions = source?.ai_grading_settings?.criteria;
    const instructionEntries =
        instructions && typeof instructions === 'object' && !Array.isArray(instructions) ? instructions : {};
    const seen = new Set();

    return [
        ...criteria.map((criterion) => ({
            id: `${criterion.id}`,
            title: criterion.title || `${criterion.id}`,
            description: criterion.description,
            instruction: instructionEntries[criterion.id],
        })),
        ...Object.entries(instructionEntries).map(([id, instruction]) => ({
            id: `${id}`,
            title: `${id}`,
            instruction,
        })),
    ].filter((criterion) => {
        if (seen.has(criterion.id)) return false;
        seen.add(criterion.id);
        return true;
    });
};

const getListEntries = (instruction, key) =>
    Array.isArray(instruction?.[key])
        ? instruction[key].filter((entry) => typeof entry === 'string' && entry.trim())
        : [];

const getMappingTargetLabel = ({ criterion, currentRubric, mapping, targetCriteriaById, mappingRequiredLabel }) => {
    if (!currentRubric || !criterion.instruction || !hasCriterionGradingSettings(criterion.instruction))
        return undefined;
    const targetCriterion = targetCriteriaById.get(`${mapping[criterion.id]}`);
    return targetCriterion?.title || targetCriterion?.id || mappingRequiredLabel;
};

const InstructionRows = ({ entries, warning }) => (
    <Stack spacing={2}>
        {entries.length ? (
            entries.map((entry, index) => (
                <InstructionRow key={`${entry}-${index}`} alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon
                        kind={warning ? 'error' : 'checkmark'}
                        sx={(theme) => ({
                            flexShrink: 0,
                            fontSize: '1rem',
                            color: theme.palette.ensightful[warning ? 'error' : 'success'][700],
                        })}
                    />
                    <Text sx={{ flex: 1 }}>{entry}</Text>
                </InstructionRow>
            ))
        ) : (
            <Text color={'light'}>--</Text>
        )}
    </Stack>
);

const InstructionPanel = ({ children, warning, t }) => (
    <ListPanel $warning={warning} spacing={2}>
        <Text weight={'medium'}>
            {warning
                ? t('criterion.violations_omissions.title', 'What does a violation or omission look like?')
                : t('criterion.look_fors.title', 'What would you see or hear if this were done well?')}
        </Text>
        {children}
    </ListPanel>
);

const CriterionCard = ({ criterion, expanded, mappingLabel, onChange }) => {
    const { t: assignmentT } = useTranslation('assignment');
    const { t: gradingT } = useTranslation('videoGrading');
    const instruction = criterion.instruction || {};
    const lookFors = getListEntries(instruction, 'look_fors');
    const violations = getListEntries(instruction, 'violations_omissions');
    const overallInstruction =
        typeof instruction.grading_instructions === 'string' && stripHTML(instruction.grading_instructions).trim();

    return (
        <CriterionAccordion expanded={expanded} onChange={onChange}>
            <CriterionAccordionSummary>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                    <Text color={'light'}>
                        {assignmentT(
                            'assignment-rollover.criterion-grading-instructions',
                            'Criterion Grading Instructions'
                        )}
                    </Text>
                    <Text weight={'medium'}>{criterion.title}</Text>
                    {criterion.description && <Text color={'light'}>{criterion.description}</Text>}
                    {mappingLabel && (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                            <Text color={'light'} variant={'p3'}>
                                {assignmentT('assignment-rollover.remapping-criterion', 'Remapping Criterion')}
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} flexWrap={'wrap'} useFlexGap spacing={1}>
                                <Text color={'light'} variant={'p3'} weight={'medium'}>
                                    {criterion.title}
                                </Text>
                                <Icon kind={'arrowRight'} />
                                <Text color={'light'} variant={'p3'} weight={'medium'}>
                                    {mappingLabel}
                                </Text>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </CriterionAccordionSummary>
            <CriterionAccordionDetails>
                <Stack spacing={4}>
                    {overallInstruction && (
                        <Stack spacing={1}>
                            <Text color={'light'}>
                                {gradingT('criterion.overall-label', 'Overall Criterion Instruction')}
                            </Text>
                            <Text color={'light'} rich>
                                {instruction.grading_instructions}
                            </Text>
                        </Stack>
                    )}
                    <InstructionPanel t={gradingT}>
                        <InstructionRows entries={lookFors} />
                    </InstructionPanel>
                    <InstructionPanel t={gradingT} warning>
                        <InstructionRows entries={violations} warning />
                    </InstructionPanel>
                </Stack>
            </CriterionAccordionDetails>
        </CriterionAccordion>
    );
};

const getMappingStatus = ({ importsRubric, mappingAnalysis, showConfirmationStatus, t }) => {
    if (showConfirmationStatus && mappingAnalysis) {
        const orphanCount =
            mappingAnalysis.unmappedOrphanSourceIds?.length ?? mappingAnalysis.orphanSourceIds?.length ?? 0;
        if (importsRubric && orphanCount) {
            return {
                color: 'error',
                message: t(
                    'assignment-rollover.orphan-grading-instructions',
                    '{{count}} saved criterion instructions have no matching source rubric criteria and will not be imported.',
                    { count: orphanCount }
                ),
                showStandaloneMessage: true,
            };
        }
        if (mappingAnalysis.unmappedCount) {
            return {
                color: 'error',
                message: t(
                    'assignment-rollover.unmapped-grading-instructions',
                    '{{count}} criterion instructions will not be imported.',
                    { count: mappingAnalysis.unmappedCount }
                ),
                showStandaloneMessage: false,
            };
        }
        return {
            color: 'light',
            message: t(
                'assignment-rollover.mapped-grading-instructions',
                '{{count}} criterion instructions are ready to import.',
                { count: mappingAnalysis.mappedCount }
            ),
            showStandaloneMessage: false,
        };
    }

    return {
        color: 'error',
        message: t(
            'assignment-rollover.verify-grading-mapping',
            '! Verify that the grading instructions match the current rubric criteria.'
        ),
        showStandaloneMessage: false,
    };
};

const GradingSettingsImportPreview = ({
    currentRubric,
    importsRubric = false,
    mapping = {},
    mappingAnalysis,
    source,
    onView,
    showConfirmationStatus = false,
}) => {
    const { t } = useTranslation('assignment');
    const { t: gradingT } = useTranslation('videoGrading');
    const [expandedCriterionId, setExpandedCriterionId] = useState(null);
    const criteria = getInstructionCriteria(source);
    const targetCriteriaById = useMemo(
        () => new Map((currentRubric?.criteria || []).map((criterion) => [`${criterion.id}`, criterion])),
        [currentRubric]
    );
    const instructions = source?.ai_grading_settings?.criteria;
    const hasInstructions =
        instructions &&
        typeof instructions === 'object' &&
        !Array.isArray(instructions) &&
        Object.values(instructions).some(hasCriterionGradingSettings);

    const mappingStatus = getMappingStatus({ importsRubric, mappingAnalysis, showConfirmationStatus, t });

    useEffect(() => {
        setExpandedCriterionId(null);
    }, [source]);

    return (
        <Stack spacing={4}>
            {hasInstructions && onView && !importsRubric && (
                <Stack alignItems={'center'} direction={'row'} flexWrap={'wrap'} spacing={2}>
                    <Text color={mappingStatus.color} variant={'p3'}>
                        {mappingStatus.message}
                    </Text>
                    <Button variant={'borderless'} onClick={onView}>
                        {t('common:controls.view', 'View')}
                    </Button>
                </Stack>
            )}
            {mappingStatus.showStandaloneMessage && (
                <Text color={mappingStatus.color} variant={'p3'}>
                    {mappingStatus.message}
                </Text>
            )}
            <GeneralInstructionsPanel>
                <Text weight={'medium'}>
                    {t('assignment-rollover.general-grading-instructions', 'General Grading Instructions')}
                </Text>
                <Text color={'light'}>{stripHTML(source?.ai_grading_context) || t('common:ui.none', 'None')}</Text>
            </GeneralInstructionsPanel>
            {criteria.map((criterion) => (
                <CriterionCard
                    key={criterion.id}
                    criterion={criterion}
                    expanded={expandedCriterionId === criterion.id}
                    mappingLabel={getMappingTargetLabel({
                        criterion,
                        currentRubric,
                        mapping,
                        targetCriteriaById,
                        mappingRequiredLabel: gradingT('criterion.mapping-required', 'Mapping required'),
                    })}
                    onChange={(_, nextExpanded) => setExpandedCriterionId(nextExpanded ? criterion.id : null)}
                />
            ))}
        </Stack>
    );
};

const GeneralInstructionsPanel = styled(Stack)(({ theme }) => ({
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    borderRadius: theme.spacing(4),
    background: theme.palette.ensightful.neutral[50],
}));

const CriterionAccordion = styled(SharedCriterionAccordion)(({ theme }) => ({
    background: theme.palette.ensightful.neutral[50],
    borderRadius: `${theme.spacing(4)} !important`,
    boxShadow: 'none !important',
    '&::before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
}));

const CriterionAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: theme.spacing(4),
    '&.Mui-expanded': { minHeight: 'unset' },
    '& > .MuiAccordionSummary-content': { margin: 0 },
}));

const CriterionAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0, 4, 4),
}));

const ListPanel = styled(Stack, {
    shouldForwardProp: (prop) => prop !== '$warning',
})(({ $warning, theme }) => ({
    padding: theme.spacing(5),
    borderRadius: theme.spacing(4),
    background: $warning ? theme.palette.ensightful.warning[50] : theme.palette.ensightful.success[50],
}));

const InstructionRow = styled(Stack)(({ theme }) => ({
    minHeight: theme.spacing(10.5),
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: theme.spacing(2),
    background: theme.palette.ensightful.shades[0],
}));

export default GradingSettingsImportPreview;
